// providers/connectionSync.ts
/**
 * @fileoverview Resolves whether a user connection is used for account syncing.
 *
 * The Connections view shows an "Account Syncing" indicator per user connection. Most integrations
 * designate a single sync user (`configuration.syncUserId`), but some — e.g. Redtail — sync via all
 * connected users. Each provider can supply its own {@link ConnectionSyncPredicate} on its provider
 * config; this module applies the provider's override when present and falls back to the default
 * `syncUserId` match otherwise.
 */
import type * as Api from '@ViewModels';
import { getProviderConfig } from './index';
import type { ConnectionSyncPredicate } from './types';

/**
 * Default predicate: a connection is sync-enabled when it is the designated sync user.
 */
const defaultConnectionSyncPredicate: ConnectionSyncPredicate = (userConnection, configuration) =>
	!!configuration.syncUserId && userConnection.userId === configuration.syncUserId;

/**
 * Decide whether a user connection counts as sync-enabled for the given provider.
 *
 * Uses the provider's `isConnectionSyncEnabled` override when defined, otherwise the default
 * `configuration.syncUserId` match.
 *
 * @param provider - The integration provider
 * @param userConnection - The user connection being evaluated
 * @param configuration - The integration configuration
 * @returns true if the connection is used for account syncing
 */
export function isConnectionSyncEnabled(
	provider: Api.IntegrationProvider,
	userConnection: Api.IUserConnection,
	configuration: Api.IIntegrationConfiguration
): boolean {
	const predicate = getProviderConfig(provider).isConnectionSyncEnabled ?? defaultConnectionSyncPredicate;
	return predicate(userConnection, configuration);
}
